import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function LightingRepairsPage() {
  useEffect(() => {
    document.title = 'Lighting Repairs Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional lighting repairs in Watford. Fix flickering lights, faulty switches, dimmer problems & light fixture issues. Same-day service. Call 020 3198 1818.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Professional lighting repairs in Watford. Fix flickering lights, faulty switches, dimmer problems & light fixture issues. Same-day service. Call 020 3198 1818.';
      document.head.appendChild(meta);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/repairs/lighting-repairs",
      "telephone": "02031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      },
      "serviceType": "Lighting Repairs",
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    { emoji: '💡', title: 'Light Fixture Repairs', description: 'Fixing faulty ceiling lights, wall lights, and pendant fixtures' },
    { emoji: '🔌', title: 'Switch Repairs', description: 'Repairing or replacing broken light switches and dimmers' },
    { emoji: '⚡', title: 'Flickering Lights', description: 'Diagnosing and fixing lights that flicker or dim unexpectedly' },
    { emoji: '🔧', title: 'Wiring Repairs', description: 'Fixing damaged lighting circuits and loose connections' },
    { emoji: '💡', title: 'LED Conversions', description: 'Upgrading to energy-efficient LED lighting during repairs' },
    { emoji: '🛠️', title: 'Emergency Lighting', description: 'Repairing emergency lighting systems and exit signs' }
  ];

  const issues = [
    { emoji: '💡', title: 'Lights Not Working', description: 'Individual lights or entire circuits not functioning' },
    { emoji: '⚡', title: 'Flickering or Dimming', description: 'Lights flickering, dimming, or brightness fluctuating' },
    { emoji: '🔌', title: 'Switch Problems', description: 'Light switches not working, loose, or sparking' },
    { emoji: '🔥', title: 'Buzzing Sounds', description: 'Unusual humming or buzzing from lights or switches' },
    { emoji: '💥', title: 'Bulbs Blowing', description: 'Light bulbs burning out frequently' },
    { emoji: '⚠️', title: 'Partial Lighting', description: 'Some lights in a room working, others not' }
  ];

  const benefits = [
    { emoji: '⚡', title: 'Fast Repairs', description: 'Most lighting issues diagnosed and fixed in a single visit' },
    { emoji: '💰', title: 'Cost-Effective', description: 'Repairs often more economical than complete replacements' },
    { emoji: '🛡️', title: 'Safety Assured', description: 'All repairs meet BS 7671 wiring regulations with certification' }
  ];

  const areas = [
    'Watford', 'Rickmansworth', 'Bushey', 'Abbots Langley', 'Croxley Green',
    'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Radlett', 'Borehamwood', 'St Albans'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white pt-32 pb-20">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/6af861beea6c01aeccdfa66173062a4d.jpg')] bg-cover bg-center opacity-10"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Lighting Repairs in Watford
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Expert lighting repair services for homes and businesses. Fast diagnosis and repair of all lighting issues.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-shield-check-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">NICEIC Approved</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-time-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">Same-Day Service</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <i className="ri-map-pin-line text-[#D4AF37]"></i>
                <span className="text-sm whitespace-nowrap">Serving Watford</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:02031981818" className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line"></i>
                Get a Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Our Lighting Repair Services
              </h2>
              <p className="text-xl text-gray-600">
                Comprehensive solutions for all your lighting problems
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                  <div className="w-16 h-16 flex items-center justify-center text-4xl mb-4">
                    {service.emoji}
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Common Lighting Problems We Fix
              </h2>
              <p className="text-xl text-gray-600">
                Expert solutions for all types of lighting issues
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {issues.map((issue, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="w-16 h-16 flex items-center justify-center text-4xl mb-4">
                    {issue.emoji}
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {issue.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {issue.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-4">
                Why Choose Our Lighting Repair Service
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                  <div className="w-20 h-20 flex items-center justify-center text-5xl mx-auto mb-6">
                    {benefit.emoji}
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Beyond Electrical */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                Why Choose Beyond Electrical
              </h2>
              <p className="text-xl text-gray-300">
                Trusted lighting repair experts in Watford
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-time-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">Fast Response</h3>
                <p className="text-gray-300">Same-day service for urgent lighting repairs</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-shield-check-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">NICEIC Certified</h3>
                <p className="text-gray-300">All work certified and compliant with regulations</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-[#D4AF37] rounded-full mx-auto mb-4">
                  <i className="ri-award-line text-4xl text-[#0A1628]"></i>
                </div>
                <h3 className="text-2xl font-bold mb-3">15+ Years Experience</h3>
                <p className="text-gray-300">Trusted by hundreds of Watford homeowners</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">600+</div>
                <div className="text-gray-300">Lighting Repairs</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">100%</div>
                <div className="text-gray-300">Satisfaction Rate</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">24/7</div>
                <div className="text-gray-300">Emergency Service</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-[#D4AF37] mb-2">15+</div>
                <div className="text-gray-300">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1628] mb-8">
              Areas We Cover
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Providing lighting repair services throughout Watford and surrounding areas
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {areas.map((area, index) => (
                <span key={index} className="bg-gray-100 text-[#0A1628] px-6 py-3 rounded-full font-medium hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer">
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0F1F3A] to-[#0A1628] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Need Lighting Repairs in Watford?
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              Contact us today for fast, professional lighting repair services. Same-day appointments available.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:02031981818" className="bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i>
                Call 020 3198 1818
              </a>
              <a href="mailto:info@beyondelectrical.co.uk" className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-mail-line"></i>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
